"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import { FileUploader } from "@/components/ui/FileUploader";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getToolUiCopy } from "@/lib/tool-ui-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

export function RemoveBackground() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>("");
  const [processedImage, setProcessedImage] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const ui = getToolUiCopy(locale);
  const localizedTool = getLocalizedTool(
    tools.find((item) => item.id === "remove-background") ?? tools[0],
    locale
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
      setProcessedImage("");
    }
  };

  const removeBackground = () => {
    if (!file) return;

    setIsProcessing(true);

    // Simple canvas-based background removal (edge detection approach)
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Simple approach: remove pixels similar to corners (assumed to be background)
      const cornerColor = { r: data[0], g: data[1], b: data[2] };
      const threshold = 30;

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        const diff = Math.abs(r - cornerColor.r) + Math.abs(g - cornerColor.g) + Math.abs(b - cornerColor.b);

        if (diff < threshold) {
          data[i + 3] = 0; // Make transparent
        }
      }

      ctx.putImageData(imageData, 0, 0);
      setProcessedImage(canvas.toDataURL("image/png"));
      setIsProcessing(false);
    };

    img.src = preview;
  };

  const downloadImage = () => {
    if (!processedImage) return;

    const link = document.createElement("a");
    link.href = processedImage;
    link.download = `no-background-${file?.name || "image.png"}`;
    link.click();
  };

  return (
    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6 mb-8">
        <div className="flex items-start gap-4">
          <div className="hidden md:block rounded-full bg-positivus-green/20 p-2">
            <Icon icon="mdi:information-variant" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark w-full">
            <p className="font-medium text-lg mb-2">{localizedTool.name}</p>
            <p className="leading-relaxed">{localizedTool.description}</p>
          </div>
        </div>
      </div>

      {!file ? (
        <FileUploader
          accept="image/*"
          onFilesSelected={(files) => {
             if (files && files[0]) {
                const syntheticEvent = {
                  target: {
                    files: files,
                  }
                } as unknown as React.ChangeEvent<HTMLInputElement>;
                handleFileChange(syntheticEvent);
             }
          }}
          className="rounded-[30px] bg-white border-dashed border-positivus-dark/20 p-8"
        >
            <div className="flex flex-col items-center gap-4 w-full">
            <div className="h-16 w-16 rounded-full bg-positivus-green/20 flex items-center justify-center">
              <Icon icon="mdi:cloud-upload" className="h-8 w-8 text-positivus-dark" />
            </div>
            <div>
              <p className="text-lg font-bold text-positivus-dark">{ui.clickToUploadImage}</p>
              <p className="text-sm text-gray-500 mt-1">{ui.supportsJpgPng}</p>
            </div>
          </div>
        </FileUploader>
      ) : (
        <div className="space-y-8">
            <div className="rounded-[30px] border-2 border-positivus-dark bg-white p-6 shadow-[0_5px_0_0_#191A23]">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-positivus-green/20 flex items-center justify-center">
                    <Icon icon="mdi:image" className="h-6 w-6 text-positivus-dark" />
                  </div>
                  <div>
                     <p className="font-bold text-lg text-positivus-dark">{file.name}</p>
                     <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
                <button
                  onClick={() => { setFile(null); setPreview(""); setProcessedImage(""); }}
                  className="text-red-500 hover:text-red-700 font-medium text-sm border-2 border-transparent hover:border-red-100 rounded-[10px] px-3 py-1 transition-all"
                >
                  {ui.remove}
                </button>
              </div>

               <button
                onClick={removeBackground}
                disabled={isProcessing}
                className="w-full rounded-[20px] bg-positivus-dark px-6 py-4 text-xl font-medium text-white hover:bg-positivus-green hover:text-positivus-dark transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                   <div className="flex items-center justify-center gap-2">
                     <Icon icon="mdi:loading" className="h-6 w-6 animate-spin" />
                     <span>{ui.generate}...</span>
                   </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                     <Icon icon="mdi:magic-staff" className="h-6 w-6" />
                     <span>{localizedTool.name}</span>
                   </div>
                )}
              </button>
            </div>
        </div>
      )}

      {(preview || processedImage) && file && (
          <div className="grid gap-8 md:grid-cols-2">
            <div>
              <p className="text-lg font-bold text-positivus-dark mb-4 pl-2">{ui.originalPreview}</p>
              <div className="rounded-[20px] border-2 border-positivus-dark/20 bg-white p-4">
                <img src={preview} alt={ui.originalPreview} className="w-full rounded-[10px]" />
              </div>
            </div>

            {processedImage && (
              <div>
                <p className="text-lg font-bold text-positivus-dark mb-4 pl-2">{ui.output}</p>
                <div className="rounded-[20px] border-2 border-positivus-green bg-white p-4 shadow-[0_5px_0_0_#191A23] relative overflow-hidden">
                   <div className="absolute inset-0 z-0 opacity-20" style={{ backgroundImage: 'repeating-conic-gradient(#808080 0% 25%, transparent 0% 50%) 50% / 20px 20px' }}></div>
                  <img src={processedImage} alt={ui.output} className="w-full rounded-[10px] relative z-10" />
                </div>
                <button
                  onClick={downloadImage}
                  className="w-full mt-6 flex items-center justify-center gap-2 rounded-[20px] bg-positivus-green px-6 py-4 text-xl font-bold text-positivus-dark hover:bg-positivus-dark hover:text-white transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px]"
                >
                  <Icon icon={uiIcons.download} className="h-6 w-6" />
                  {ui.downloadCompressedImage}
                </button>
              </div>
            )}
          </div>
      )}
    </div>
  );
}
